if (!customElements.get('products-toolbar')) {
  class ProductsToolbar extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      this.filtersComponent = document.querySelector('facet-filters');
      this.filtersColumn = document.querySelector('.main-products-grid__filters');
      this.layoutSwitcher = this.querySelector('.products-toolbar__layout');
      this.compareToggle = document.getElementById('compare-switch');
      this.sortBy = this.querySelector('.products-toolbar__sort');
      this.productsGrid = document.querySelector('.main-products-grid');

      if (this.filtersColumn) {
        this.filterToggle = this.querySelector('.js-toggle-filters');
        this.filterToggle.addEventListener('click', this.toggleFilters.bind(this));

        if (this.filtersComponent) {
          this.filtersComponent.addEventListener(
            'on:facet-filters:before-close',
            this.handleFiltersClose.bind(this)
          );
        }
      }

      if (this.layoutSwitcher) {
        this.layoutSwitcher.addEventListener('change', this.handleLayoutChange.bind(this));

        // Set the layout to the layout in localstorage
        const layout = theme.storageUtil.get('products-layout');
        if (layout && layout === 'list') {
          const toggle = document.getElementById(`${layout}-layout-opt`);
          if (toggle) toggle.click();
          this.handleLayoutChange(null, layout);
        }
      }

      if (this.compareToggle) {
        this.compareToggle.addEventListener('change', this.toggleCompare.bind(this));

        const isChecked = theme.storageUtil.get('compare-toggle', true);
        if (isChecked !== null) {
          this.toggleCompare(null, isChecked, true);
        } else {
          this.toggleCompare(null, this.compareToggle.checked, false);
        }

        setTimeout(() => {
          this.classList.add('transition-ready');
          this.productsGrid.classList.add('transition-ready');
        }, 500);
      } else if (theme.settings.compareToggle === 'none') {
        this.toggleCompare(null, true, false);
      }

      if (!this.filtersComponent && this.sortBy) {
        this.sortBy.addEventListener('change', ProductsToolbar.handleSortByChange);
      }

      this.initMobileSortPortal();
    }

    /**
     * Sets up a mobile Sort button on /a/search that opens the existing
     * <custom-select id="products-sort-by"> dropdown.
     *
     * Why the portal:
     * On /a/search the Xcloud (CloudSearch) Search & Product Filter app injects a rule
     * (see snippets/rapid-search-results-template-v2.liquid) that hides every direct
     * child of the shopify-section other than its own .rps-results container — for
     * theme_version 2 this is a `display: none !important` CSS rule, for theme_version 1
     * it is inline `style.display = 'none'` re-applied by a MutationObserver. The .container
     * wrapping <products-toolbar> is hit by both, so the theme's sort dropdown ends up in
     * the DOM but invisible. Moving the .products-toolbar__sort element into a portal
     * appended to <body> takes it out of the shopify-section entirely so neither blocker
     * applies. Event listeners attached during init survive the relocation.
     */
    initMobileSortPortal() {
      if (window.location.pathname !== '/a/search') return;
      if (!this.sortBy) return;

      this.mobileSortBtn = document.querySelector('.js-open-mobile-sort');
      this.portaledSort = this.querySelector('.products-toolbar__sort--search-portal');
      if (!this.mobileSortBtn || !this.portaledSort) return;

      this.sortCustomSelect = this.portaledSort.querySelector('custom-select');
      if (!this.sortCustomSelect) return;

      this.sortOriginalParent = this.portaledSort.parentNode;
      this.sortOriginalNextSibling = this.portaledSort.nextSibling;

      this.sortPortal = document.createElement('div');
      this.sortPortal.id = 'products-sort-portal';
      document.body.appendChild(this.sortPortal);

      this.syncMobileSortPlacement();
      window.addEventListener('on:breakpoint-change', this.syncMobileSortPlacement.bind(this));

      this.mobileSortBtn.addEventListener('mousedown', this.handleMobileSortMousedown.bind(this));
      this.mobileSortBtn.addEventListener('click', this.handleMobileSortBtnClick.bind(this));

      this.sortCustomSelectBtn = this.sortCustomSelect.querySelector('.custom-select__btn');
      if (this.sortCustomSelectBtn) {
        this.sortAriaObserver = new MutationObserver(this.handleSortAriaChange.bind(this));
        this.sortAriaObserver.observe(this.sortCustomSelectBtn, {
          attributes: true,
          attributeFilter: ['aria-expanded']
        });
      }
    }

    /**
     * Moves the sort wrapper into the portal at mobile width, restores it at desktop width.
     */
    syncMobileSortPlacement() {
      const isMobile = !theme.mediaMatches.md;
      const inPortal = this.portaledSort.parentNode === this.sortPortal;

      if (isMobile && !inPortal) {
        this.positionPortalAtButton();
        this.sortPortal.appendChild(this.portaledSort);
      } else if (!isMobile && inPortal) {
        if (this.sortOriginalNextSibling && this.sortOriginalNextSibling.parentNode === this.sortOriginalParent) {
          this.sortOriginalParent.insertBefore(this.portaledSort, this.sortOriginalNextSibling);
        } else {
          this.sortOriginalParent.appendChild(this.portaledSort);
        }
      }
    }

    /**
     * Anchors the portal next to the mobile Sort button so the dropdown listbox
     * opens beneath it.
     */
    positionPortalAtButton() {
      const rect = this.mobileSortBtn.getBoundingClientRect();
      this.sortPortal.style.position = 'absolute';
      this.sortPortal.style.top = `${rect.bottom + window.scrollY}px`;
      this.sortPortal.style.left = `${rect.left + window.scrollX}px`;
      this.sortPortal.style.width = `${rect.width}px`;
      this.sortPortal.style.zIndex = '20';
    }

    /**
     * Captures whether the dropdown was open at the moment the user pressed
     * down on the mobile Sort button. We need this because clicking the button
     * blurs the listbox (which auto-closes via custom-select's blur handler)
     * BEFORE the click event fires — so by click time, listboxOpen is already
     * false and we can't tell from state alone whether the user wanted to open
     * or close. The mousedown flag preserves the pre-blur intent.
     */
    handleMobileSortMousedown() {
      this.sortWasOpenAtMousedown = !!(this.sortCustomSelect && this.sortCustomSelect.listboxOpen);
    }

    /**
     * Toggles the existing custom-select dropdown when the mobile Sort button is tapped.
     */
    handleMobileSortBtnClick() {
      if (!this.sortCustomSelect) return;

      // If the dropdown was open when the user pressed down, the blur cascade
      // has already closed it — treat this click as a close and don't re-open.
      if (this.sortWasOpenAtMousedown) {
        this.sortWasOpenAtMousedown = false;
        return;
      }

      this.positionPortalAtButton();
      // custom-select lazy-inits via IntersectionObserver. If the observer hasn't
      // fired yet, showListbox() will throw because internal state isn't set up.
      if (!this.sortCustomSelect.options) {
        this.sortCustomSelect.init();
      }
      this.sortCustomSelect.showListbox();
    }

    /**
     * Mirrors the dropdown's open/closed state onto the mobile Sort button and returns
     * focus to the mobile button when the dropdown closes.
     */
    handleSortAriaChange() {
      const expanded = this.sortCustomSelectBtn.getAttribute('aria-expanded') === 'true';
      this.mobileSortBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (!expanded && document.activeElement === this.sortCustomSelectBtn) {
        this.mobileSortBtn.focus();
      }
    }

    /**
     * Updates the UI to reflect the compare state
     * @param {object} evt - Change event object
     * @param {boolean} checked - Whether compare is enabled or not (optional)
     * @param {boolean} persist - Whether to persist the new state (optional)
     */
    toggleCompare(evt, checked = null, persist = true) {
      const isChecked = evt ? evt.target.checked : checked;
      this.productsGrid.dataset.compare = isChecked;

      const compareButton = document.getElementById('compare-drawer-open');
      compareButton.classList.toggle('is-out', !isChecked);

      if (!evt && this.compareToggle) this.compareToggle.checked = isChecked;

      if (persist && !Shopify.designMode) {
        theme.storageUtil.set('compare-toggle', isChecked);
      }

      // Load scripts/styles if not already loaded
      if (isChecked) {
        const resourcesTag = document.getElementById('compare-resources');
        if (resourcesTag) {
          const resources = JSON.parse(resourcesTag.textContent);
          resources.forEach((resource) => {
            if (resource.type === 'js') {
              const script = document.createElement('script');
              script.src = resource.path;
              document.head.appendChild(script);
            } else if (resource.type === 'css') {
              const link = document.createElement('link');
              link.href = resource.path;
              link.type = 'text/css';
              link.rel = 'stylesheet';
              document.head.appendChild(link);
            }
          });
          resourcesTag.remove();
        }
      }
    }

    /**
     * Toggles open/closed state of the filters on desktop.
     */
    toggleFilters() {
      this.filterToggle.classList.toggle('is-active');
      this.filtersOpen = this.filterToggle.classList.contains('is-active');

      if (theme.mediaMatches.lg) {
        this.filterToggle.setAttribute('aria-expanded', this.filtersOpen);
        this.filtersColumn.classList.toggle('lg:js-hidden', !this.filtersOpen);
        const productsList = document.querySelector('.main-products-grid__results > .grid');

        productsList.className = this.filtersOpen
          ? productsList.dataset.filtersOpenClasses
          : productsList.dataset.filtersClosedClasses;
      } else {
        this.filterToggle.setAttribute('aria-expanded', 'true');
        this.filtersComponent.open(this.filterToggle);
      }
    }

    handleFiltersClose() {
      if (theme.mediaMatches.lg || !this.filterToggle) return;
      this.filterToggle.setAttribute('aria-expanded', 'false');
    }

    /**
     * Handles 'change' events on the layout switcher buttons.
     * @param {object} evt - Event object.
     * @param {string} layout - 'grid' or 'list' - the layout to change to.
     */
    handleLayoutChange(evt, layout) {
      this.productsGrid.dataset.layout = layout || evt.target.value;
      if (!layout) theme.storageUtil.set('products-layout', evt.target.value);
    }

    /**
     * Handles when a sort by dropdown is changed (and filtering is disabled)
     * @param {object} evt - Event object.
     */
    static handleSortByChange(evt) {
      const urlObj = new URL(window.location.href);
      urlObj.searchParams.set('sort_by', evt.detail.selectedValue);
      // On /a/search the toolbar's natural DOM position is inside the shopify-section
      // that Xcloud hides, so a #products-toolbar hash would scroll the user to an
      // invisible element after reload. Skip the hash there; preserve the existing
      // scroll-to-toolbar UX on collection pages.
      if (window.location.pathname === '/a/search') {
        urlObj.hash = '';
      } else {
        urlObj.hash = 'products-toolbar';
      }
      window.location.href = urlObj.toString();
    }
  }

  customElements.define('products-toolbar', ProductsToolbar);
}
